import { useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { formatCalendarDate } from '@/lib/format';

import { installLicense, removeLicense, useLicense } from './use-license';

/** About → Licence. Renders nothing in a build that cannot check licences. */
export function LicenseSection() {
  const state = useLicense();
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  if (state.status === 'loading' || state.status === 'unavailable') {
    return null;
  }

  return (
    <section className="border-border bg-surface rounded-container border p-4">
      <h2 className="text-sm font-semibold tracking-tight">Licence</h2>
      {state.status === 'valid' ? (
        <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
          <dt className="text-muted-foreground">Licensed to</dt>
          <dd>
            {state.license.name} ({state.license.email})
          </dd>
          <dt className="text-muted-foreground">Edition</dt>
          <dd className="capitalize">{state.license.edition}</dd>
          <dt className="text-muted-foreground">Updates</dt>
          <dd>
            {state.license.updatesUntil === null
              ? 'All versions'
              : `Versions released until ${formatCalendarDate(state.license.updatesUntil)}`}
          </dd>
        </dl>
      ) : (
        <p className="text-muted-foreground mt-1 text-sm">
          {state.status === 'invalid' ? 'Licence file is not valid.' : 'No licence.'}
        </p>
      )}
      {error === null ? null : (
        <p role="alert" className="text-danger mt-2 text-xs">
          {error}
        </p>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            inputRef.current?.click();
          }}
        >
          {state.status === 'valid' ? 'Replace licence' : 'Add licence'}
        </Button>
        {state.status === 'none' ? null : (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              void removeLicense();
            }}
          >
            Remove
          </Button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept=".fdvlicense,application/json"
          className="sr-only"
          aria-label="Licence file"
          tabIndex={-1}
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (file === undefined) {
              return;
            }
            setError(null);
            void file.text().then(async (text) => {
              if (!(await installLicense(text))) {
                setError('Licence file is not valid.');
              }
            });
          }}
        />
      </div>
    </section>
  );
}
